import { Dispatch, ElementType, FC, Fragment, RefObject, SetStateAction } from 'react';

import { Drawer, DrawerBody, DrawerFooter, ProgressBar } from '@growtherapy/sprout-ui';

import { DrawerHeader } from './DrawerHeader';
import { usePaginatedPages } from './usePaginatedPages';

type ProgressBarProps = Parameters<typeof ProgressBar>[0];

export type BasePaginatedDrawerProps<T> = {
  /**
   * The component to render the actions in drawer's footer.
   */
  actionsComponent?: FC<PageComponentProps<T>>;
  /**
   * If true, then this component will only return the header, body, and
   * footer of the drawer.
   */
  contentOnly?: boolean;
  /**
   * The index of the page to display initially.
   */
  initialPageIndex?: number;
  /**
   * The component to render the content of the drawer's body.
   */
  contentComponent: FC<PageComponentProps<T>>;

  drawerBodyProps?: Omit<Parameters<typeof DrawerBody>[0], 'children'>;
  /**
   * The component to render the header of the drawer.
   */
  headerComponent: FC<PageComponentProps<T>>;
  backButtonDisabled?: boolean;
  progressBarProps?: Partial<ProgressBarProps>;

  /**
   * A function that should return a unique key for the body of the drawer. This
   * is used to force the drawer body to re-render when the key changes, and is
   * useful for resetting scroll position.
   */
  drawerBodyKeyFn?: (props: PageComponentProps<T>) => string;

  /**
   * The element type of the drawer heading
   */
  headingAs?: ElementType;

  /**
   * Called whenever the current page index changes. Can be used to persist
   * progress in global state so it can be restored if the component remounts.
   */
  onPageIndexChange?: (pageIndex: number) => void;

  pages: T[];
};

export type PaginatedDrawerPropsWithoutDrawer<T> = BasePaginatedDrawerProps<T> & {
  contentOnly: true;
};

export type PaginatedDrawerPropsWithDrawer<T> = BasePaginatedDrawerProps<T> & {
  contentOnly?: false;
} & Parameters<typeof Drawer>[0];

export type PaginatedDrawerProps<T> = PaginatedDrawerPropsWithoutDrawer<T> | PaginatedDrawerPropsWithDrawer<T>;

export type PageComponentProps<T> = {
  page: T;
  pageCount: number;
  pageIndex: number;
  isFirstPage: boolean;
  isLastPage: boolean;
  nextPage: () => void;
  prevPage: () => void;
  backButtonDisabled?: boolean;
  setPage: Dispatch<SetStateAction<T>>;
  scrollToTop: () => void;
  scrollContainerRef?: RefObject<HTMLElement>;
};

export function PaginatedDrawer<T>({
  actionsComponent: ActionsComponent,
  contentComponent: ContentComponent,
  drawerBodyProps,
  contentOnly,
  backButtonDisabled,
  initialPageIndex = 0,
  pages: initialPages,
  headerComponent: HeaderComponent,
  headingAs: HeadingElement,
  drawerBodyKeyFn,
  onPageIndexChange,
  ...drawerProps
}: PaginatedDrawerProps<T>) {
  const { componentProps, contentRef } = usePaginatedPages<T>({
    pages: initialPages,
    initialPageIndex,
    backButtonDisabled,
    onPageIndexChange,
  });
  const [Container, otherProps] = contentOnly ? [Fragment, {}] : [Drawer, drawerProps];
  const drawerBodyKeyProps = drawerBodyKeyFn
    ? {
        key: drawerBodyKeyFn(componentProps),
      }
    : {};

  return (
    <Container {...otherProps}>
      <DrawerHeader headingProps={{ as: HeadingElement }}>
        <HeaderComponent {...componentProps} />
      </DrawerHeader>
      <DrawerBody
        {...{
          ...drawerBodyProps,
          ...drawerBodyKeyProps,
        }}
        ref={contentRef}
      >
        <ContentComponent {...componentProps} />
      </DrawerBody>
      <DrawerFooter>
        <div className='ml-auto'>{ActionsComponent && <ActionsComponent {...componentProps} />}</div>
      </DrawerFooter>
    </Container>
  );
}
